import { className } from '@empori/util';
import * as React from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { ImageWithInfo } from '../empracoTypes';
import { FaIcon } from '../generic/icon';
import { ImageGalleryFullscreen } from './ImageGalleryFullscreen';
import { Slide } from '../empracoTypes';
import { Direction } from '../util/util';

interface SlideProps {
  image: ImageWithInfo;
  index: number;
}

function SlideItem({ image, index }: SlideProps) {
  return <img
    loading={index === 0 ? 'eager' : 'lazy'}
    className='image-item'
    src={image.imgUrl}
    alt={image.imgAlt} />;
}

interface NavArrowProps {
  direction: Direction;
}

function NavArrow ({ direction }: NavArrowProps) {
  const swiper = useSwiper();

  const handleChange = () => {
    if (direction === Direction.next) {
      swiper.slideNext();
      return;
    }

    swiper.slidePrev();
  };

  return (
    <div 
      className={`swiper-button-${Direction[direction]}`}
      onClick={handleChange}
      onKeyDown={(e) => {
        if (e.keyCode === 13 || e.key === 'Enter') {
          handleChange();
        }
      }}
      tabIndex={0}>
      <FaIcon clsName={`fa-regular fa-arrow-${direction === Direction.next ? 'right' : 'left'}`} />
    </div>
  );
}

interface ThumbnailProps {
  index: number;
  image: ImageWithInfo;
  active: boolean;
}

function Thumbnail({ index, image, active }: ThumbnailProps) {
  const swiper = useSwiper();

  return(
    <div className={className('thumbnail-item', { 'active': active })}
      onClick={() => swiper.slideTo(index + 1)}>
      <div className='thumbnail-image'
        style={{ backgroundImage: `url(${image.imgUrl})`}} title={image.imgAlt}></div>
    </div>
  );
}

interface ThumbnailImageGalleryProps {
  images: ImageWithInfo[];
}

export default function ThumbnailImageGallery({ images }: ThumbnailImageGalleryProps) {
  const [ activeIndex, setActiveIndex ] = React.useState<number>(0);
  const [ fullscreen, setFullscreen ] = React.useState<boolean>(false);

  if (!images || images && images.length < 1) {
    return null;
  }

  return (
    <Swiper
      loop={images.length > 1}
      spaceBetween={0}
      slidesPerView={1}
      onSlideChangeTransitionStart={swiper => setActiveIndex(swiper.realIndex)}
      lazy>
      {images.map((image, index) =>
        <SwiperSlide key={`slide${index + 1}`}>
          <SlideItem index={index} image={image} />
        </SwiperSlide>)}
      <NavArrow direction={Direction.prev} />
      <NavArrow direction={Direction.next} />
      <div className='thumbnails element-flex'>
        {images.map((image, index) =>
          <Thumbnail key={`slide${index + 1}`} image={image} index={index} active={index === activeIndex} />)}
      </div>
      <ImageGalleryFullscreen
        toggled={fullscreen}
        slideImgUrls={images.map(img => img.imgUrlOriginal)}
        viewingIndex={activeIndex}
        close={() => setFullscreen(false)}
        open={() => setFullscreen(true)}
        setIndex={setActiveIndex} />
    </Swiper>
  );
}